import {
  Package,
  ShoppingCart,
  ShoppingBag,
  ArrowRightLeft,
  Settings2,
  TrendingUp,
  TrendingDown,
  Layers,
  Calendar,
  Building2,
  Eye,
  X,
  Barcode,
  User,
} from 'lucide-react'
import React, { useState, useCallback, useEffect } from 'react'
import { toast } from 'sonner'

// ── Componentes Globales ──────────────────────────────────────────────────────
import { CatalogSelect } from '../components/ui/CatalogSelect'
import { LocalSelect } from '../components/ui/LocalSelect'
import { SearchFilter } from '../components/ui/SearchFilter'
import { Button } from '../components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog'
import { GlobalPaginator } from '../components/ui/global-paginator'
import { TableSkeleton } from '../components/ui/table-skeleton'
// ── Servicio ──────────────────────────────────────────────────────────────────
import {
  inventoryMovementsService,
  InventoryMovement,
  MovementType,
} from '../services/inventory-movements.service'

// ─────────────────────────────────────────────────────────────────────────────
// Configuración de tipos de movimiento
// ─────────────────────────────────────────────────────────────────────────────

interface MovementConfig {
  label: string
  shortLabel: string
  bgColor: string
  textColor: string
  ringColor: string
  Icon: React.ElementType
  direction: 'in' | 'out' | 'neutral'
}

const MOVEMENT_CONFIG: Record<MovementType, MovementConfig> = {
  purchase: {
    label: 'Ingreso por compra',
    shortLabel: 'Compra',
    bgColor: 'bg-emerald-500/10',
    textColor: 'text-emerald-600 dark:text-emerald-500',
    ringColor: 'ring-emerald-500/20',
    Icon: ShoppingBag,
    direction: 'in',
  },
  sale: {
    label: 'Salida por venta',
    shortLabel: 'Venta',
    bgColor: 'bg-blue-500/10',
    textColor: 'text-blue-600 dark:text-blue-500',
    ringColor: 'ring-blue-500/20',
    Icon: ShoppingCart,
    direction: 'out',
  },
  manual_stock_adjustment_in: {
    label: 'Ajuste manual (entrada)',
    shortLabel: 'Ajuste +',
    bgColor: 'bg-violet-500/10',
    textColor: 'text-violet-600 dark:text-violet-500',
    ringColor: 'ring-violet-500/20',
    Icon: Settings2,
    direction: 'in',
  },
  manual_stock_adjustment_out: {
    label: 'Ajuste manual (salida)',
    shortLabel: 'Ajuste −',
    bgColor: 'bg-amber-500/10',
    textColor: 'text-amber-600 dark:text-amber-500',
    ringColor: 'ring-amber-500/20',
    Icon: Settings2,
    direction: 'out',
  },
  transfer_in: {
    label: 'Ingreso por transferencia',
    shortLabel: 'Transferencia +',
    bgColor: 'bg-cyan-500/10',
    textColor: 'text-cyan-600 dark:text-cyan-500',
    ringColor: 'ring-cyan-500/20',
    Icon: ArrowRightLeft,
    direction: 'in',
  },
  transfer_out: {
    label: 'Salida por transferencia',
    shortLabel: 'Transferencia −',
    bgColor: 'bg-orange-500/10',
    textColor: 'text-orange-600 dark:text-orange-500',
    ringColor: 'ring-orange-500/20',
    Icon: ArrowRightLeft,
    direction: 'out',
  },
  transfer_not_received: {
    label: 'Transferencia no recibida',
    shortLabel: 'Trans. Fallida',
    bgColor: 'bg-red-500/10',
    textColor: 'text-red-600 dark:text-red-500',
    ringColor: 'ring-red-500/20',
    Icon: ArrowRightLeft,
    direction: 'neutral',
  },
  import_initial_stock: {
    label: 'Stock inicial por importación',
    shortLabel: 'Inicial (Imp)',
    bgColor: 'bg-muted',
    textColor: 'text-muted-foreground',
    ringColor: 'ring-border',
    Icon: Layers,
    direction: 'in',
  },
  import_stock_adjustment: {
    label: 'Ajuste por importación',
    shortLabel: 'Ajuste (Imp)',
    bgColor: 'bg-indigo-500/10',
    textColor: 'text-indigo-600 dark:text-indigo-500',
    ringColor: 'ring-indigo-500/20',
    Icon: Layers,
    direction: 'neutral',
  },
}

const DEFAULT_MOVEMENT_CONFIG: MovementConfig = {
  label: 'Movimiento',
  shortLabel: 'Otro',
  bgColor: 'bg-muted',
  textColor: 'text-muted-foreground',
  ringColor: 'ring-border',
  Icon: Package,
  direction: 'neutral',
}

function getMovementConfig(type: string): MovementConfig {
  return MOVEMENT_CONFIG[type as MovementType] ?? DEFAULT_MOVEMENT_CONFIG
}

function getRelatedSucursalName(movement: InventoryMovement): string {
  if (movement.type === 'transfer_in' || movement.type === 'transfer_out') {
    const destination = movement.destination_sucursal_name
      || (movement.type === 'transfer_in' ? movement.sucursal_name : null)
    const origin = movement.origin_sucursal_name
      || (movement.type === 'transfer_out' ? movement.sucursal_name : null)

    return [...new Set([destination, origin].filter(Boolean))].join(' / ') || '—'
  }

  return movement.sucursal_name || '—'
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers de fecha
// ─────────────────────────────────────────────────────────────────────────────

function todayISO() {
  return new Date().toLocaleDateString('sv-SE')
}

function yesterdayISO() {
  const d = new Date()
  d.setDate(d.getDate() - 1)
  return d.toLocaleDateString('sv-SE')
}

function formatDate(raw: string): string {
  try {
    const d = new Date(raw)
    const now = new Date()
    const isToday = d.toDateString() === now.toDateString()
    const yesterday = new Date(now)
    yesterday.setDate(now.getDate() - 1)
    const isYesterday = d.toDateString() === yesterday.toDateString()
    const time = d.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })
    if (isToday) return `Hoy · ${time}`
    if (isYesterday) return `Ayer · ${time}`
    return d.toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' }) + ` · ${time}`
  } catch {
    return raw
  }
}

function formatQuantity(value: number | string | null): string {
  if (value === null) return 'Sin capturar'

  const numericValue = Number(value)

  return Number.isFinite(numericValue)
    ? new Intl.NumberFormat('es-MX', { maximumFractionDigits: 3 }).format(numericValue)
    : String(value)
}

// ─────────────────────────────────────────────────────────────────────────────
// Modal de Detalle
// ─────────────────────────────────────────────────────────────────────────────

function MovementDetailModal({
  movement,
  onClose,
}: {
  movement: InventoryMovement | null
  onClose: () => void
}) {
  if (!movement) return null
  const cfg = getMovementConfig(movement.type)
  const affectsStock = movement.affects_stock !== false

  return (
    <Dialog open={!!movement} onOpenChange={onClose}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg bg-popover border-popover-border">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-popover-foreground">
            <cfg.Icon className="h-5 w-5 text-primary" />
            Detalle del movimiento
          </DialogTitle>
          <DialogDescription className="sr-only">
            Información de trazabilidad, producto y existencias del movimiento seleccionado.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Tipo + dirección */}
          <div className="flex items-center gap-3">
            <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${cfg.bgColor}`}>
              <cfg.Icon className={`h-5 w-5 ${cfg.textColor}`} />
            </div>
            <div className="min-w-0">
              <p className="font-semibold text-popover-foreground">{cfg.label}</p>
              <p className="text-xs text-popover-foreground/70">{formatDate(movement.created_at)}</p>
            </div>
            <div className="ml-auto shrink-0">
              {!affectsStock ? (
                <span className="rounded-full bg-primary/10 px-2.5 py-1 text-xs font-semibold text-inherit opacity-70">
                  Sin movimiento
                </span>
              ) : cfg.direction === 'in' ? (
                <span className="flex items-center gap-1 rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-semibold text-emerald-700">
                  <TrendingUp className="h-3 w-3" /> Entrada
                </span>
              ) : cfg.direction === 'out' ? (
                <span className="flex items-center gap-1 rounded-full bg-red-500/10 px-2.5 py-1 text-xs font-semibold text-red-500 border border-red-500/20">
                  <TrendingDown className="h-3 w-3" /> Salida
                </span>
              ) : (
                <span className="rounded-full bg-popover-foreground/10 px-2.5 py-1 text-xs font-semibold text-popover-foreground border border-popover-border">
                  Neutro
                </span>
              )}
            </div>
          </div>

          {/* Producto */}
          <section className="space-y-2">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-popover-foreground/70">Producto</h3>
            <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
              <div className="col-span-2">
                <dt className="text-popover-foreground/70">Nombre</dt>
                <dd className="font-medium text-popover-foreground">{movement.product_name}</dd>
              </div>
              {movement.product_barcode && (
                <div>
                  <dt className="text-popover-foreground/70">Código de barras</dt>
                  <dd className="font-mono text-popover-foreground flex items-center gap-2"><Barcode className='h-3 w-3' /> {movement.product_barcode}</dd>
                </div>
              )}
              {movement.batch_number && (
                <div>
                  <dt className="text-popover-foreground/70">Lote</dt>
                  <dd className="font-mono text-popover-foreground">{movement.batch_number}</dd>
                </div>
              )}
            </dl>
          </section>

          {/* Cantidad y stock */}
          <section className="space-y-2">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-popover-foreground/70">Cantidad</h3>
            <dl className="grid grid-cols-3 gap-x-4 gap-y-2 text-sm">
              <div>
                <dt className="text-inherit opacity-70">Movimiento</dt>
                <dd className={`text-lg font-bold ${affectsStock && cfg.direction === 'in' ? 'text-emerald-600' : affectsStock && cfg.direction === 'out' ? 'text-destructive' : 'text-inherit'}`}>
                  {formatQuantity(movement.quantity)}
                </dd>
              </div>
              <div>
                <dt className="text-inherit opacity-70">SALDO</dt>
                <dd className="text-lg font-bold text-inherit">
                  {movement.stock_after === null ? 'Sin capturar' : `${movement.stock_after} u.`}
                </dd>
              </div>
              <div>
                <dt className="text-popover-foreground/70">Sucursal Destino</dt>
                <dd className="text-sm font-medium text-popover-foreground">{movement.destination_sucursal_name || movement.sucursal_name || '—'}</dd>
              </div>
              <div>
                <dt className="text-popover-foreground/70">Suc. de Procedencia</dt>
                <dd className="text-sm font-medium text-popover-foreground">{movement.origin_sucursal_name || '—'}</dd>
              </div>
            </dl>
          </section>

          {/* Proveedor / motivo */}
          {(movement.supplier_name || movement.purchase_reference_number || movement.reason) && (
            <section className="rounded-lg border border-border bg-card p-4">
              <h3 className="mb-3 text-xs font-semibold uppercase tracking-wider text-inherit opacity-70">Contexto</h3>
              <dl className="space-y-2 text-sm">
                {movement.supplier_name && (
                  <div>
                    <dt className="text-popover-foreground/70">Proveedor</dt>
                    <dd className="font-medium text-popover-foreground">{movement.supplier_name}</dd>
                  </div>
                )}
                {movement.purchase_reference_number && (
                  <div>
                    <dt className="text-slate-500">Número de nota</dt>
                    <dd className="font-mono font-medium text-slate-900">{movement.purchase_reference_number}</dd>
                  </div>
                )}
                {movement.reason && (
                  <div>
                    <dt className="text-popover-foreground/70">Motivo</dt>
                    <dd className="text-popover-foreground">{movement.reason}</dd>
                  </div>
                )}
              </dl>
            </section>
          )}

          {/* Usuario y referencia */}
          <section className="space-y-2 pt-4 border-t border-popover-border">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-popover-foreground/70">Trazabilidad</h3>
            <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
              {movement.user_name && (
                <div className="col-span-2">
                  <dt className="text-popover-foreground/70">Usuario</dt>
                  <dd className="flex items-center gap-1.5 font-medium text-popover-foreground">
                    <User className="h-3 w-3 text-popover-foreground/70" />
                    {movement.user_name}
                    {movement.user_email && (
                      <span className="text-popover-foreground/50 font-normal">· {movement.user_email}</span>
                    )}
                  </dd>
                </div>
              )}
              {movement.reference_id && (
                <div>
                  <dt className="text-popover-foreground/70">Referencia</dt>
                  <dd className="font-mono text-popover-foreground">#{movement.reference_id}</dd>
                  <dd className="font-mono text-popover-foreground/70">({movement.reference_type})</dd>
                </div>
              )}
              <div>
                <dt className="text-slate-400">ID del registro</dt>
                <dd className="font-mono">#{movement.id}</dd>
              </div>
            </dl>
          </section>
        </div>
      </DialogContent>
    </Dialog>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Paginación local
// ─────────────────────────────────────────────────────────────────────────────

interface PaginationState {
  currentPage: number
  total: number
  perPage: number
  lastPage: number
  from: number
  to: number
}

const INITIAL_PAGINATION: PaginationState = {
  currentPage: 1, total: 0, perPage: 10, lastPage: 1, from: 0, to: 0,
}

// ─────────────────────────────────────────────────────────────────────────────
// Componente Principal
// ─────────────────────────────────────────────────────────────────────────────

export function InventoryMovements() {
  // ── Estado de datos ────────────────────────────────────────────────────────
  const [movements, setMovements] = useState<InventoryMovement[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [pagination, setPagination] = useState<PaginationState>(INITIAL_PAGINATION)
  const [selectedMovement, setSelectedMovement] = useState<InventoryMovement | null>(null)

  // ── Filtros ────────────────────────────────────────────────────────────────
  const [searchQuery, setSearchQuery] = useState('')
  const [filterType, setFilterType] = useState<MovementType | 'all'>('all')
  const [filterFrom, setFilterFrom] = useState('')
  const [filterTo, setFilterTo] = useState('')
  /** ID de sucursal seleccionada — string vacío = todas */
  const [filterSucursalId, setFilterSucursalId] = useState('')
  /** ID de producto seleccionado — string vacío = todos */
  const [filterProductId, setFilterProductId] = useState('')

  // ── Acceso rápido de fecha ────────────────────────────────────────────────
  const setQuickDate = (range: 'today' | 'yesterday') => {
    if (range === 'today') {
      const t = todayISO()
      setFilterFrom(t)
      setFilterTo(t)
    } else {
      const y = yesterdayISO()
      setFilterFrom(y)
      setFilterTo(y)
    }
  }

  const clearDates = () => {
    setFilterFrom('')
    setFilterTo('')
  }

  // ── Fetch ──────────────────────────────────────────────────────────────────
  const fetchMovements = useCallback(async (page = 1) => {
    setIsLoading(true)
    try {
      const params = {
        page,
        per_page: 10,
        search: searchQuery || undefined,
        type: filterType !== 'all' ? filterType : undefined,
        from: filterFrom || undefined,
        to: filterTo || undefined,
        sucursal_id: filterSucursalId ? Number(filterSucursalId) : undefined,
        product_id: filterProductId ? Number(filterProductId) : undefined,
      }

      const res = await inventoryMovementsService.getMovements(params)

      if (res?.data && Array.isArray(res.data)) {
        setMovements(res.data)
        setPagination({
          currentPage: res.current_page,
          total: res.total,
          perPage: res.per_page,
          lastPage: res.last_page,
          from: res.from ?? 0,
          to: res.to ?? 0,
        })
      } else {
        setMovements([])
        setPagination(INITIAL_PAGINATION)
      }
    } catch {
      toast.error('Error al cargar los movimientos de inventario')
    } finally {
      setIsLoading(false)
    }
  }, [searchQuery, filterType, filterFrom, filterTo, filterSucursalId, filterProductId])

  useEffect(() => {
    fetchMovements(1)
  }, [fetchMovements])

  const handlePageChange = (page: number) => fetchMovements(page)

  const hasActiveFilters = !!(
    searchQuery || filterType !== 'all' || filterFrom || filterTo || filterSucursalId || filterProductId
  )

  const resetFilters = () => {
    setSearchQuery('')
    setFilterType('all')
    setFilterFrom('')
    setFilterTo('')
    setFilterSucursalId('')
    setFilterProductId('')
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-6 p-6 w-full max-w-full min-w-0">
      {/* ── Header ── */}
      <div className="flex flex-col gap-1">
        <h1 className="text-2xl font-semibold text-foreground">Trazabilidad de Movimientos</h1>
        <p className="text-sm text-muted-foreground">
          Historial completo de entradas, salidas y ajustes de inventario por producto.
        </p>
      </div>

      {/* ── Panel de Filtros ── */}
      <div className="rounded-xl border border-border bg-card p-4 shadow-sm">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">

          <SearchFilter
            initialValue={searchQuery}
            placeholder="Producto, código, proveedor o nota..."
            onSearch={setSearchQuery}
          />

          {/* Producto */}
          <CatalogSelect
            catalog="products"
            placeholder="Todos los productos"
            value={filterProductId}
            onChange={(val: string) =>
              setFilterProductId(val === filterProductId ? '' : val)
            }
          />

          {/* Tipo de movimiento */}
          <LocalSelect
            options={(Object.entries(MOVEMENT_CONFIG) as [MovementType, MovementConfig][]).map(
              ([key, cfg]) => ({ value: key, label: cfg.label })
            )}
            value={filterType === 'all' ? '' : filterType}
            onChange={(val) => setFilterType(val ? (val as MovementType) : 'all')}
            placeholder="Todos los tipos"
          />

          {/* Sucursal */}
          <CatalogSelect
            catalog="sucursales"
            placeholder="Todas las sucursales"
            value={filterSucursalId}
            onChange={(val: string) =>
              setFilterSucursalId(val === filterSucursalId ? '' : val)
            }
          />

          {/* Limpiar filtros */}
          <div className="flex items-center gap-2">
            {hasActiveFilters && (
              <button
                onClick={resetFilters}
                className="flex h-9 items-center gap-1.5 rounded-md border border-border px-3 text-sm text-inherit opacity-70 hover:bg-primary/5 hover:text-inherit transition-colors"
              >
                <X className="h-3.5 w-3.5" /> Limpiar
              </button>
            )}
          </div>
        </div>

        {/* Segunda fila: rango de fechas + accesos rápidos */}
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-1.5">
            <Calendar className="h-4 w-4 text-slate-400 shrink-0" />
            <span className="text-sm text-inherit opacity-70">Desde:</span>
            <input
              type="date"
              value={filterFrom}
              max={filterTo || todayISO()}
              onChange={(e) => setFilterFrom(e.target.value)}
              className="h-8 rounded-md border border-border bg-card px-2 text-sm text-inherit focus:outline-none focus:ring-2 focus:ring-primary/40"
            />
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-sm text-inherit opacity-70">Hasta:</span>
            <input
              type="date"
              value={filterTo}
              min={filterFrom}
              max={todayISO()}
              onChange={(e) => setFilterTo(e.target.value)}
              className="h-8 rounded-md border border-border bg-card px-2 text-sm text-inherit focus:outline-none focus:ring-2 focus:ring-primary/40"
            />
          </div>

          {/* Accesos rápidos de fecha */}
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => setQuickDate('today')}
              className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${filterFrom === todayISO() && filterTo === todayISO()
                ? 'bg-primary text-white'
                : 'border border-border text-inherit opacity-70 hover:bg-primary/5'
                }`}
            >
              Hoy
            </button>
            <button
              onClick={() => setQuickDate('yesterday')}
              className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${filterFrom === yesterdayISO() && filterTo === yesterdayISO()
                ? 'bg-primary text-white'
                : 'border border-border text-inherit opacity-70 hover:bg-primary/5'
                }`}
            >
              Ayer
            </button>
            {(filterFrom || filterTo) && (
              <button
                onClick={clearDates}
                className="rounded-full border border-border px-3 py-1 text-xs text-slate-400 hover:bg-primary/5 hover:text-inherit opacity-70 transition-colors"
              >
                × Limpiar fechas
              </button>
            )}
          </div>
        </div>
      </div>


      {/* ── Tabla ── */}
      {isLoading ? (
        <TableSkeleton
          headers={['Fecha', 'Producto', 'Tipo', 'Cantidad', 'SALDO', 'SUCURSAL DESTINO / SUC. DE PROCEDENCIA', 'Usuario', 'Acciones']}
          columns={['text', 'text-two-lines', 'badge', 'text', 'text', 'text', 'text', 'actions']}
          rows={8}
        />
      ) : (
        <div className="w-full overflow-hidden rounded-xl border border-border bg-table-bg shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full whitespace-nowrap">
              <thead className="border-b border-border bg-table-header-bg">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-table-header-foreground">Fecha</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-table-header-foreground">Producto</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-table-header-foreground">Tipo</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-table-header-foreground">Cantidad</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-table-header-foreground">SALDO</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-table-header-foreground">SUCURSAL DESTINO / SUC. DE PROCEDENCIA</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-table-header-foreground">Usuario</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-table-header-foreground">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {movements.length > 0 ? (
                  movements.map((mov) => {
                    const cfg = getMovementConfig(mov.type)
                    const isIn = cfg.direction === 'in' && mov.affects_stock
                    const isOut = cfg.direction === 'out'

                    return (
                      <tr
                        key={mov.row_key}
                        className="group transition-colors hover:bg-primary/5/60"
                      >
                        {/* Fecha */}
                        <td className="px-4 py-3 text-sm text-table-foreground">
                          {formatDate(mov.created_at)}
                        </td>

                        {/* Producto */}
                        <td className="px-4 py-3 flex flex-col">
                          <p className="font-medium text-table-foreground table-cell-truncate" title={mov.product_name}>
                            {mov.product_name}
                          </p>
                          {mov.product_barcode && (
                            <p className="mt-0.5 font-mono text-[11px] text-table-foreground/70 table-cell-truncate flex items-center gap-2" title={mov.product_barcode}>
                              <Barcode className="h-3 w-3" /> {mov.product_barcode}
                            </p>
                          )}
                          {mov.batch_number && (
                            <p className="mt-0.5 text-[11px] text-table-foreground/70 table-cell-truncate" title={`Lote: ${mov.batch_number}`}>
                              Lote: {mov.batch_number}
                            </p>
                          )}
                        </td>

                        {/* Tipo de movimiento */}
                        <td className="px-4 py-3">
                          <span
                            className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium border ${cfg.bgColor} ${cfg.textColor} ${cfg.ringColor}`}
                          >
                            <cfg.Icon className="h-3 w-3" />
                            {cfg.shortLabel}
                          </span>
                          {/* Indicador de proveedor visual */}
                          {mov.type === 'purchase' && mov.supplier_name && (
                            <p className="mt-1 text-[11px] text-table-foreground/70">
                              {mov.supplier_name}
                            </p>
                          )}
                        </td>

                        {/* Cantidad */}
                        <td className="px-4 py-3 ">
                          <span
                            className={`inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-sm font-bold border ${isIn
                              ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-500 border-emerald-500/20'
                              : isOut
                                ? 'bg-red-500/10 text-red-600 dark:text-red-500 border-red-500/20'
                                : 'bg-muted text-muted-foreground border-border'
                              }`}
                          >
                            {isIn ? (
                              <TrendingUp className="h-3.5 w-3.5" />
                            ) : isOut ? (
                              <TrendingDown className="h-3.5 w-3.5" />
                            ) : null}
                            {formatQuantity(mov.quantity)}
                          </span>
                        </td>

                        {/* Stock resultante */}
                        <td className="px-4 py-3">
                          <span className="text-sm font-semibold text-inherit">
                            {mov.stock_after === null ? 'Sin capturar' : `${mov.stock_after} u.`}
                          </span>
                        </td>

                        {/* Sucursal relacionada */}
                        <td className="px-4 py-3">
                          <span className="flex items-center gap-1.5 text-sm text-table-foreground">
                            <Building2 className="h-3.5 w-3.5 text-table-foreground/70 shrink-0" />
                            {getRelatedSucursalName(mov)}
                          </span>
                        </td>

                        {/* Usuario */}
                        <td className="px-4 py-3">
                          {mov.user_name ? (
                            <span className="flex items-center gap-1.5 text-sm text-table-foreground">
                              <User className="h-3.5 w-3.5 text-table-foreground/70 shrink-0" />
                              <span className="table-cell-truncate" title={mov.user_name}>{mov.user_name}</span>
                            </span>
                          ) : (
                            <span className="text-xs text-table-foreground/70 italic">Sistema</span>
                          )}
                        </td>

                        {/* Acción */}
                        <td className="px-4 py-3">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setSelectedMovement(mov)}
                            className="rounded p-1.5 text-table-foreground/70 transition-colors hover:bg-muted"
                            title="Ver detalle"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                        </td>
                      </tr>
                    )
                  })
                ) : (
                  <tr>
                    <td colSpan={8} className="px-4 py-16 text-center">
                      <Package className="mx-auto mb-3 h-10 w-10 text-table-foreground/30" />
                      <p className="text-sm font-medium text-table-foreground">
                        No se encontraron movimientos
                      </p>
                      <p className="mt-1 text-xs text-table-foreground/60">
                        Ajusta los filtros o el rango de fechas
                      </p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Paginación */}
          {pagination.total > 0 && (
            <GlobalPaginator
              totalItems={pagination.total}
              itemsPerPage={pagination.perPage}
              currentPage={pagination.currentPage}
              from={pagination.from}
              to={pagination.to}
              onPageChange={handlePageChange}
            />
          )}
        </div>
      )}

      {/* Modal de detalle */}
      <MovementDetailModal
        movement={selectedMovement}
        onClose={() => setSelectedMovement(null)}
      />
    </div>
  )
}
